import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { AuthStateService, isDevelopmentBuild, isReleaseRoute } from '@dental-ui/auth';
import {
  ACCOUNT_APP_NAME,
  ACCOUNT_ENTRY_PATH,
  AUTOBAR_LOGO_MINI_PATH,
  AUTOBAR_LOGO_PATH,
  I18N_LAB_LAYOUT_BRAND_TITLE,
} from '@dental-ui/shared/consts';
import { ConfirmDialogService, NotificationsComponent } from '@dental-ui/ui';
import { calculateInitials, getInitial } from '@dental-ui/util';
import {
  LucideCalendarDays,
  LucideChevronDown,
  LucideChevronsLeft,
  LucideClipboardList,
  LucideLayoutDashboard,
  LucideLogOut,
  LucidePanelsTopLeft,
  LucideSquarePen,
  LucideSettings,
  LucidePlus,
  LucideUser,
  LucideWallet,
  LucideWrench,
  LucideCreditCard,
} from '@lucide/angular';
import { Subscription, filter } from 'rxjs';
import type { LabMenuItem } from './lab-menu-item';

const SIDEBAR_COLLAPSED_KEY = 'dc-lab-sidebar-collapsed';
const SIDEBAR_COLLAPSE_MQ = '(max-width: 780px)';

@Component({
  selector: 'lib-lab-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    NotificationsComponent,
    LucideCalendarDays,
    LucideChevronDown,
    LucideChevronsLeft,
    LucideClipboardList,
    LucideLayoutDashboard,
    LucideCreditCard,
    LucideLogOut,
    LucideSquarePen,
    LucidePanelsTopLeft,
    LucideSettings,
    LucidePlus,
    LucideUser,
    LucideWallet,
    LucideWrench,
  ],
  templateUrl: './lab-layout.component.html',
  styleUrl: './lab-layout.component.scss',
})
export class LabLayoutComponent implements OnDestroy {
  @Input() menuItems: LabMenuItem[] = [];
  @Input() appTitle = 'Lab Manager';

  readonly developmentBuild = isDevelopmentBuild();
  readonly brandLogoPath = AUTOBAR_LOGO_PATH;
  readonly brandLogoMiniPath = AUTOBAR_LOGO_MINI_PATH;
  readonly brandTitleKey = I18N_LAB_LAYOUT_BRAND_TITLE;

  private readonly authStateService = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly platformId = inject(PLATFORM_ID);

  private routerSubscription?: Subscription;
  private sidebarViewportQuery?: MediaQueryList;
  private userSidebarCollapsed = true;

  readonly currentUrl = signal(this.router.url);
  readonly isUserDropdownOpen = signal(false);
  readonly userAvatarUrl = signal<string | null>(null);
  readonly avatarImageFailed = signal(false);
  readonly isSidebarCollapsed = signal(true);
  readonly isCompactViewport = signal(false);
  readonly isCreateMenuOpen = signal(false);
  readonly ellipsisTooltip = signal<{ x: number; y: number; text: string } | null>(null);

  userFullName = '';
  userEmail = '';
  userInitials = '';

  get visibleMenuItems(): LabMenuItem[] {
    if (this.developmentBuild) {
      return this.menuItems;
    }
    return this.menuItems.filter((item) => isReleaseRoute(item.route));
  }

  @ViewChild(NotificationsComponent) private notificationsComponent?: NotificationsComponent;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.userSidebarCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) !== '0';
      this.sidebarViewportQuery = window.matchMedia(SIDEBAR_COLLAPSE_MQ);
      this.syncSidebarToViewport();
      this.sidebarViewportQuery.addEventListener('change', this.onSidebarViewportChange);
    }

    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects || event.url);
      });

    effect(() => {
      const user = this.authStateService.user();
      const lang = this.translateService.getCurrentLanguage() || 'tr';
      if (user?.firstName && user?.lastName) {
        this.userFullName = `${user.firstName} ${user.lastName}`;
        this.userInitials = calculateInitials(user.firstName, user.lastName, lang);
      } else if (user?.email) {
        this.userFullName = user.email;
        this.userInitials = getInitial(user.email).toUpperCase();
      } else {
        this.userFullName = '';
        this.userInitials = '';
      }
      const url = (user?.userAvatarUrl || '').trim();
      const byte = (user?.userAvatarByte || '').trim();
      const nextAvatar =
        url ||
        (byte
          ? byte.startsWith('data:')
            ? byte
            : `data:image/jpeg;base64,${byte}`
          : null);
      if (nextAvatar !== this.userAvatarUrl()) {
        this.avatarImageFailed.set(false);
        this.userAvatarUrl.set(nextAvatar);
      }
      this.userEmail = (user?.email || '').trim();
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.sidebarViewportQuery?.removeEventListener('change', this.onSidebarViewportChange);
  }

  private onSidebarViewportChange = (): void => {
    this.syncSidebarToViewport();
  };

  private syncSidebarToViewport(): void {
    const compact = !!this.sidebarViewportQuery?.matches;
    this.isCompactViewport.set(compact);
    if (compact) {
      this.isSidebarCollapsed.set(true);
      return;
    }
    this.isSidebarCollapsed.set(this.userSidebarCollapsed);
  }

  collapseSidebarForMobile(): void {
    if (!this.isCompactViewport()) {
      return;
    }
    this.isSidebarCollapsed.set(true);
  }

  onNavItemClick(): void {
    this.collapseSidebarForMobile();
  }

  isActiveRoute(route: string): boolean {
    const currentUrl = this.currentUrl().split('?')[0];
    if (route === '/main') {
      return currentUrl === '/main' || currentUrl === '/' || currentUrl === '';
    }
    return currentUrl === route || currentUrl.startsWith(`${route}/`);
  }

  toggleSidebarCollapsed(): void {
    this.isSidebarCollapsed.update((collapsed) => {
      const next = !collapsed;
      this.userSidebarCollapsed = next;
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      }
      return next;
    });
  }

  toggleCreateMenu(): void {

    // Live/main: only design projects are creatable — skip the menu and open create directly.
    if (!this.developmentBuild) {
      this.openCreate('/design-projects');
      return;
    }

    this.isCreateMenuOpen.update((open) => {
      const opening = !open;
      if (opening) {
        this.closeUserDropdown();
        this.notificationsComponent?.close();
      }
      return opening;
    });
  }

  closeCreateMenu(): void {
    this.isCreateMenuOpen.set(false);
  }

  openCreate(route: string): void {
    this.closeCreateMenu();
    if (route === '/design-projects') {
      void this.router.navigate([route], { queryParams: { create: '1' } });
      return;
    }
    void this.router.navigate([route]);
  }

  toggleUserDropdown(): void {
    this.isUserDropdownOpen.update((open) => {
      const opening = !open;
      if (opening) {
        this.closeCreateMenu();
        this.notificationsComponent?.close();
      }
      return opening;
    });
  }

  closeUserDropdown(): void {
    this.isUserDropdownOpen.set(false);
    this.hideEllipsisTooltip();
  }

  showEllipsisTooltip(event: MouseEvent): void {
    const host = event.currentTarget as HTMLElement | null;
    if (!host || host.scrollWidth <= host.clientWidth + 1) {
      this.ellipsisTooltip.set(null);
      return;
    }
    const text = host.innerText.replace(/\s+/g, ' ').trim();
    if (!text) {
      this.ellipsisTooltip.set(null);
      return;
    }
    const rect = host.getBoundingClientRect();
    this.ellipsisTooltip.set({
      x: rect.left,
      y: Math.max(rect.top - 32, 8),
      text,
    });
  }

  hideEllipsisTooltip(): void {
    this.ellipsisTooltip.set(null);
  }

  onNotificationsOpenChange(open: boolean): void {
    if (open) {
      this.closeUserDropdown();
      this.closeCreateMenu();
    }
  }

  onAvatarImageError(): void {
    this.avatarImageFailed.set(true);
  }

  logout(): void {
    this.closeUserDropdown();
    this.confirmDialogService
      .confirm({
        title: this.translateService.getText('LAB_LAYOUT.SIGN_OUT_TITLE'),
        message: this.translateService.getText('LAB_LAYOUT.SIGN_OUT_MESSAGE'),
        confirmText: this.translateService.getText('LAB_LAYOUT.LOGOUT'),
        cancelText: this.translateService.getText('LAB_LAYOUT.SIGN_OUT_STAY'),
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.authStateService.logout();
        }
      });
  }

  openAccount(): void {
    if (!this.canOpenAccount()) {
      return;
    }
    this.closeUserDropdown();
    this.authStateService.openApplication(ACCOUNT_APP_NAME, ACCOUNT_ENTRY_PATH);
  }

  canOpenAccount(): boolean {
    return this.authStateService.user()?.role === 'AccountManager';
  }

  openSettings(): void {
    this.closeUserDropdown();
    void this.router.navigate(['/settings']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-container')) {
      this.closeUserDropdown();
    }
    if (!target.closest('.create-menu-wrap')) {
      this.closeCreateMenu();
    }
  }
}
